/**
 * useSelection — Manages selection state for the PageBuilder.
 *
 * Owns:
 *   - selectedSection (the full section object, or null)
 *   - selectedColumn  ({ sectionId, columnIndex } or null)
 *   - editingSection  (section open in the full modal editor, or null)
 *
 * Derived:
 *   - selectedComponentPath (from section.content.selectedComponentPath)
 *   - selectedComponent (the component object at that path)
 *
 * Also handles:
 *   - Auto-clearing selectedColumn when selectedSection changes
 *   - Clearing all selection on page change (via `resetSelection`)
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { getComponentAtPath } from '../utils/treeUtils';

export default function useSelection() {
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [editingSection, setEditingSection] = useState(null);

  // Auto-clear column selection when section selection changes
  useEffect(() => {
    setSelectedColumn(null);
  }, [selectedSection?.id]);

  // Derived: component path from section content
  const selectedComponentPath = selectedSection?.content?.selectedComponentPath || null;

  // Derived: the actual component object at the selected path
  const selectedComponent = useMemo(() => {
    return getComponentAtPath(
      selectedSection?.content?.components,
      selectedComponentPath,
    );
  }, [selectedSection, selectedComponentPath]);

  // Select a section by its ID from a sections array
  const selectSectionById = useCallback((sectionId, sections) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section) {
      setSelectedSection(section);
    }
  }, []);

  // Select a component within a section
  const selectComponent = useCallback((sectionId, componentPath, sections) => {
    const section = sections.find((s) => s.id === sectionId);
    if (section && section.section_type === 'layout-1') {
      setSelectedSection({
        ...section,
        content: { ...section.content, selectedComponentPath: componentPath },
      });
    } else if (section) {
      setSelectedSection(section);
    }
  }, []);

  // Clear all selection state
  const clearSelection = useCallback(() => {
    setSelectedSection(null);
    setSelectedColumn(null);
    setEditingSection(null);
  }, []);

  // Reset selection (used on page change)
  const resetSelection = useCallback(() => {
    setSelectedSection(null);
    setSelectedColumn(null);
    setEditingSection(null);
  }, []);

  return {
    // State
    selectedSection,
    setSelectedSection,
    selectedColumn,
    setSelectedColumn,
    editingSection,
    setEditingSection,

    // Derived
    selectedComponentPath,
    selectedComponent,

    // Actions
    selectSectionById,
    selectComponent,
    clearSelection,
    resetSelection,
  };
}
